/**
 * Flow-v3-Wizard: Session-Persistenz im Browser (sessionStorage).
 *
 * Zweck: Der Projekt-Assistent (ProjectWizardView) hält seinen Fortschritt in
 * flüchtigem React-State. Beim Weg-Navigieren (Sidebar-Klick, Overlay-Builder,
 * Browser-Back) wird die Komponente unmountet und der State ginge verloren.
 *
 * Hier persistieren wir NUR die "resumable" UI-Position + die Zugehörigkeit der
 * Bilder zum aktuellen Durchlauf (image ids). Die eigentlichen Bilddaten leben
 * in der DB (images / image_history / storage_path_empty/staged) und werden beim
 * Wiedereintritt daraus rekonstruiert — die Session sagt nur "welcher Schritt,
 * welcher Stil, welche Räume gehören zum Batch".
 *
 * sessionStorage (nicht localStorage): pro Tab, wird beim Schließen aufgeräumt —
 * passt zu "browser memory". Die DB ist der dauerhafte Rückfall (nichts geht
 * wirklich verloren; ohne Session startet der Flow eben frisch).
 *
 * Robustheit: Beim Laden wird JEDES Feld validiert/normalisiert. Kritische
 * Fehler (falsches Projekt, keine roomImageIds, abgelaufen, inkompatible
 * Version, kaputtes JSON) verwerfen die Session (und räumen den Eintrag auf).
 * Unkritische Felder (Stil, Format, Branding-Feintuning) fallen auf sichere
 * Defaults zurück, statt die ganze Session wegzuwerfen — ein leicht
 * beschädigter Eintrag kostet so nie den kompletten Fortschritt.
 */

export type WizardStepNum = 1 | 2 | 3 | 4;
export type WizardStyleId = 'modern' | 'luxury' | 'industrial';
export type WizardAspectRatio = '1:1' | '4:3';
export type WizardLogoPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type WizardWatermarkPosition = 'center' | 'tile' | 'tl' | 'tr' | 'bl' | 'br';

export interface WizardSessionSnapshot {
  projectId: string;
  step: WizardStepNum;
  style: WizardStyleId;
  aspect: WizardAspectRatio;
  overlayChoice: 'none' | 'own';
  exportCompressed: boolean;
  /** DB-Bild-IDs dieses Durchlaufs, in Anzeige-Reihenfolge. */
  roomImageIds: string[];
  /** Branding-Auswahl aus Schritt 4 (optional — ältere Sessions haben sie nicht). */
  selectedOverlayId?: string | null;
  brandingLogoAssetId?: string | null;
  brandingLogoPosition?: WizardLogoPosition;
  brandingWatermarkAssetId?: string | null;
  brandingWatermarkOpacity?: number;
  brandingWatermarkScale?: number;
  brandingWatermarkPosition?: WizardWatermarkPosition;
  updatedAt: number;
}

const STYLES: readonly WizardStyleId[] = ['modern', 'luxury', 'industrial'];
const ASPECTS: readonly WizardAspectRatio[] = ['1:1', '4:3'];
const LOGO_POSITIONS: readonly WizardLogoPosition[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const WATERMARK_POSITIONS: readonly WizardWatermarkPosition[] = ['center', 'tile', 'tl', 'tr', 'bl', 'br'];

/**
 * Normalisiert den gespeicherten Schritt auf die aktuelle 4-Schritt-Leiste:
 * gültige Schritte (1-4) bleiben unverändert, alles andere fällt auf Schritt 1
 * zurück. Die frühere 5→4-Migration alter Sessions ist obsolet (sessionStorage
 * lebt nur pro Tab und läuft nach 24 h ab) und hat Schritt 4 fälschlich auf 3
 * heruntergestuft — daher entfernt.
 */
export function migrateWizardStep(step: number): WizardStepNum {
  if (step === 1 || step === 2 || step === 3 || step === 4) return step;
  return 1;
}

const KEY_PREFIX = 'estage:wizard:';
/** Persistenz-Schema. Bei inkompatiblen Änderungen erhöhen → Alt-Sessions verfallen sauber. */
const SCHEMA_VERSION = 1;
/** Sicherheits-Verfall: veraltete Sessions nach 24 h ignorieren/aufräumen. */
const MAX_AGE_MS = 1000 * 60 * 60 * 24;

const storageKey = (projectId: string): string => `${KEY_PREFIX}${projectId}`;

/**
 * Sicherer Zugriff auf sessionStorage. In manchen Privacy-Modi wirft schon der
 * Property-Zugriff — daher gekapselt. Gibt null zurück, wenn nicht verfügbar.
 */
function getStore(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

// ── Validierungs-Helfer ─────────────────────────────────────────────────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

function optOneOf<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function optClamp(v: unknown, min: number, max: number): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : undefined;
}

/**
 * Validiert + normalisiert eine rohe (geparste) Session gegen die erwartete
 * projectId. Gibt null zurück, wenn die Session kritisch ungültig oder
 * abgelaufen ist.
 */
function sanitizeSnapshot(data: unknown, expectedProjectId: string): WizardSessionSnapshot | null {
  if (!isRecord(data)) return null;

  // Inkompatible Schema-Version → verwerfen. Fehlende Version = Legacy (best effort).
  if (typeof data.version === 'number' && data.version !== SCHEMA_VERSION) return null;

  // Projekt muss exakt passen (Schutz vor Key/Value-Mismatch).
  if (typeof data.projectId !== 'string' || data.projectId !== expectedProjectId) return null;

  // updatedAt: endliche Zahl Pflicht; zu alt → verwerfen. (Zukunfts-Timestamps
  // durch Uhr-Skew sind unkritisch und laufen nicht ab.)
  const updatedAt = data.updatedAt;
  if (typeof updatedAt !== 'number' || !Number.isFinite(updatedAt)) return null;
  if (Date.now() - updatedAt > MAX_AGE_MS) return null;

  // roomImageIds: kritisch für die Rekonstruktion — muss ein String-Array sein.
  if (!Array.isArray(data.roomImageIds)) return null;
  const roomImageIds = data.roomImageIds.filter((x): x is string => typeof x === 'string' && x.length > 0);

  return {
    projectId: expectedProjectId,
    step: migrateWizardStep(typeof data.step === 'number' ? data.step : 1),
    style: oneOf(data.style, STYLES, 'modern'),
    aspect: oneOf(data.aspect, ASPECTS, '4:3'),
    overlayChoice: data.overlayChoice === 'own' ? 'own' : 'none',
    exportCompressed: data.exportCompressed === true,
    roomImageIds,
    selectedOverlayId: strOrNull(data.selectedOverlayId),
    brandingLogoAssetId: strOrNull(data.brandingLogoAssetId),
    brandingLogoPosition: optOneOf(data.brandingLogoPosition, LOGO_POSITIONS),
    brandingWatermarkAssetId: strOrNull(data.brandingWatermarkAssetId),
    brandingWatermarkOpacity: optClamp(data.brandingWatermarkOpacity, 0, 100),
    brandingWatermarkScale: optClamp(data.brandingWatermarkScale, 0.05, 1),
    brandingWatermarkPosition: optOneOf(data.brandingWatermarkPosition, WATERMARK_POSITIONS),
    updatedAt,
  };
}

/** Entfernt abgelaufene/kaputte Wizard-Einträge (opportunistisch, z. B. bei Quota). */
function pruneWizardSessions(keepProjectId?: string): void {
  const store = getStore();
  if (!store) return;
  const keys: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k);
    }
  } catch {
    return;
  }
  const now = Date.now();
  for (const key of keys) {
    if (keepProjectId && key === storageKey(keepProjectId)) continue;
    let stale = true;
    try {
      const raw = store.getItem(key);
      if (raw) {
        const d = JSON.parse(raw) as { updatedAt?: unknown; version?: unknown };
        stale =
          (typeof d?.version === 'number' && d.version !== SCHEMA_VERSION) ||
          !(typeof d?.updatedAt === 'number' && Number.isFinite(d.updatedAt) && now - d.updatedAt <= MAX_AGE_MS);
      }
    } catch {
      stale = true; // unlesbar → weg
    }
    if (stale) {
      try {
        store.removeItem(key);
      } catch {
        /* ignore */
      }
    }
  }
}

export function saveWizardSession(snapshot: Omit<WizardSessionSnapshot, 'updatedAt'>): void {
  if (!snapshot?.projectId || typeof snapshot.projectId !== 'string') return;
  const store = getStore();
  if (!store) return;
  const payload = JSON.stringify({ ...snapshot, version: SCHEMA_VERSION, updatedAt: Date.now() });
  const key = storageKey(snapshot.projectId);
  try {
    store.setItem(key, payload);
  } catch {
    // Quota/Privacy: einmal stale Sessions prunen und erneut versuchen.
    try {
      pruneWizardSessions(snapshot.projectId);
      store.setItem(key, payload);
    } catch {
      // Endgültig nicht speicherbar — unkritisch, die DB ist der Rückfall.
    }
  }
}

export function loadWizardSession(projectId: string): WizardSessionSnapshot | null {
  if (!projectId || typeof projectId !== 'string') return null;
  const store = getStore();
  if (!store) return null;

  let raw: string | null;
  try {
    raw = store.getItem(storageKey(projectId));
  } catch {
    return null;
  }
  if (!raw) return null;

  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    clearWizardSession(projectId); // korruptes JSON entfernen
    return null;
  }

  const snapshot = sanitizeSnapshot(data, projectId);
  if (!snapshot) {
    clearWizardSession(projectId); // ungültig/abgelaufen/inkompatibel → aufräumen
    return null;
  }
  return snapshot;
}

export function clearWizardSession(projectId: string): void {
  if (!projectId) return;
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(storageKey(projectId));
  } catch {
    // ignore
  }
}

/** True, wenn für dieses Projekt ein (nicht abgelaufener) Assistent-Fortschritt vorliegt. */
export function hasWizardSession(projectId: string): boolean {
  return loadWizardSession(projectId) !== null;
}

/**
 * Jüngste (zuletzt aktualisierte) offene Session über alle Projekte — für die
 * "Durchlauf fortsetzen"-Bubble. Abgelaufene/kaputte Einträge werden ignoriert
 * (und von loadWizardSession aufgeräumt). Die Keys werden zuerst eingesammelt,
 * damit das Entfernen während der Iteration keine Einträge überspringt.
 */
export function getLatestWizardSession(): WizardSessionSnapshot | null {
  const store = getStore();
  if (!store) return null;

  const projectIds: string[] = [];
  try {
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key && key.startsWith(KEY_PREFIX)) projectIds.push(key.slice(KEY_PREFIX.length));
    }
  } catch {
    return null;
  }

  let latest: WizardSessionSnapshot | null = null;
  for (const projectId of projectIds) {
    const session = loadWizardSession(projectId);
    if (session && (!latest || session.updatedAt > latest.updatedAt)) {
      latest = session;
    }
  }
  return latest;
}
